/**
 * SceneErrorHandler - Standardized error handling for Babylon.js scene operations
 *
 * Provides consistent error logging, fallback handling, and async wrappers
 * for all scene-related operations (model loading, texture loading, etc.)
 */

// Error severity levels
export const ErrorLevel = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
};

// Error categories for grouping
export const ErrorCategory = {
  MODEL_LOADING: 'MODEL_LOADING',
  TEXTURE_LOADING: 'TEXTURE_LOADING',
  ASSET_LOADING: 'ASSET_LOADING',
  ENVIRONMENT: 'ENVIRONMENT',
  ENTITY: 'ENTITY',
  ANIMATION: 'ANIMATION',
  AUDIO: 'AUDIO',
  UNKNOWN: 'UNKNOWN',
};

// Emoji prefixes for log messages
const LOG_PREFIXES = {
  [ErrorLevel.INFO]: 'ℹ️',
  [ErrorLevel.WARN]: '⚠️',
  [ErrorLevel.ERROR]: '❌',
};

/**
 * Log an error with consistent formatting
 * @param {string} context - Where the error occurred (e.g., 'TreeManager.load')
 * @param {Error|string} error - The error object or message
 * @param {string} level - Error severity level
 * @param {string} category - Error category
 */
export function logError(
  context,
  error,
  level = ErrorLevel.ERROR,
  category = ErrorCategory.UNKNOWN,
) {
  const prefix = LOG_PREFIXES[level] || LOG_PREFIXES[ErrorLevel.ERROR];
  const message = error instanceof Error ? error.message : error;
  const timestamp = new Date().toISOString();

  const logMessage = `${prefix} [${category}] ${context}: ${message}`;

  switch (level) {
    case ErrorLevel.INFO:
      console.info(logMessage);
      break;
    case ErrorLevel.WARN:
      console.warn(logMessage);
      break;
    case ErrorLevel.ERROR:
    default:
      console.error(logMessage);
      if (error instanceof Error && error.stack) {
        console.error('Stack trace:', error.stack);
      }
      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(
            new CustomEvent('oggi:client-error', {
              detail: {
                context,
                message,
                category,
                stack: error instanceof Error ? error.stack : null,
                timestamp,
              },
            }),
          );
        } catch {
          // Reporting is best-effort and must never affect scene recovery.
        }
      }
      break;
  }

  // Return structured error info for potential further handling
  return {
    context,
    message,
    level,
    category,
    timestamp,
    originalError: error instanceof Error ? error : null,
  };
}

/**
 * Wrap an async function with standardized error handling
 * @param {Function} asyncFn - The async function to wrap
 * @param {Object} [options] - Configuration options
 * @param {string} [options.context] - Description of the operation
 * @param {string} [options.category] - Error category
 * @param {*} [options.fallbackValue] - Value to return on error (default: null)
 * @param {Function} [options.onError] - Optional callback on error
 * @param {Function} [options.onFallback] - Optional fallback function to execute
 * @returns {Promise<*>} Result of asyncFn or fallbackValue on error
 */
export async function withErrorHandling(asyncFn, options = {}) {
  const {
    context = 'Unknown operation',
    category = ErrorCategory.UNKNOWN,
    fallbackValue = null,
    onError = null,
    onFallback = null,
  } = options;

  try {
    return await asyncFn();
  } catch (error) {
    logError(context, error, ErrorLevel.ERROR, category);

    if (onError) {
      onError(error);
    }

    if (onFallback) {
      try {
        return await onFallback(error);
      } catch (fallbackError) {
        logError(`${context} (fallback)`, fallbackError, ErrorLevel.ERROR, category);
      }
    }

    return fallbackValue;
  }
}

/**
 * Wrap a sync function with error handling and optional fallback
 * @param {Function} fn - The function to wrap
 * @param {Object} [options] - Configuration options
 * @param {string} [options.context] - Description of the operation
 * @param {string} [options.category] - Error category
 * @param {string} [options.level] - Error level (default: ERROR)
 * @param {*} [options.fallbackValue] - Value to return on error
 * @param {Function} [options.onFallback] - Optional fallback function
 * @returns {*} Result of fn or fallbackValue on error
 */
export function trySafe(fn, options = {}) {
  const {
    context = 'Unknown operation',
    category = ErrorCategory.UNKNOWN,
    level = ErrorLevel.ERROR,
    fallbackValue = null,
    onFallback = null,
  } = options;

  try {
    return fn();
  } catch (error) {
    logError(context, error, level, category);

    if (onFallback) {
      try {
        return onFallback(error);
      } catch (fallbackError) {
        logError(`${context} (fallback)`, fallbackError, ErrorLevel.ERROR, category);
      }
    }

    return fallbackValue;
  }
}

/**
 * Create a scoped error handler for a specific class/module
 * @param {string} moduleName - Name of the module (e.g., 'TreeManager')
 * @param {string} defaultCategory - Default error category
 * @returns Scoped error handling utilities. Deliberately un-annotated so the
 *   object literal's own shape is inferred - a `{Object}` tag erases every
 *   member and makes the scope unusable from TypeScript callers.
 */
export function createErrorScope(moduleName, defaultCategory = ErrorCategory.UNKNOWN) {
  return {
    /**
     * Log an error within this scope
     */
    log: (method, error, level = ErrorLevel.ERROR, category = defaultCategory) => {
      return logError(`${moduleName}.${method}`, error, level, category);
    },

    /**
     * Wrap an async operation within this scope
     */
    wrapAsync: (method, asyncFn, options = {}) => {
      return withErrorHandling(asyncFn, {
        context: `${moduleName}.${method}`,
        category: defaultCategory,
        ...options,
      });
    },

    /**
     * Wrap a sync operation within this scope
     */
    trySafe: (method, fn, options = {}) => {
      return trySafe(fn, {
        context: `${moduleName}.${method}`,
        category: defaultCategory,
        ...options,
      });
    },

    /**
     * Log a warning within this scope
     */
    warn: (method, message) => {
      return logError(`${moduleName}.${method}`, message, ErrorLevel.WARN, defaultCategory);
    },

    /**
     * Log info within this scope
     */
    info: (method, message) => {
      return logError(`${moduleName}.${method}`, message, ErrorLevel.INFO, defaultCategory);
    },
  };
}

/**
 * Helper to load assets with consistent error handling
 * @param {Function} loadFn - The loading function
 * @param {string} assetPath - Path to the asset
 * @param {string} assetType - Type of asset (for logging)
 * @param {*} fallbackValue - Value to return on failure
 * @returns {Promise<*>} Loaded asset or fallback
 */
export async function loadAssetSafe(loadFn, assetPath, assetType = 'asset', fallbackValue = null) {
  return withErrorHandling(() => loadFn(), {
    context: `Loading ${assetType}`,
    category: ErrorCategory.ASSET_LOADING,
    fallbackValue,
    onError: () => {
      console.warn(`Failed to load ${assetType} from: ${assetPath}`);
    },
  });
}
